/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Sep 20 2023
 **/
import { useTheme } from "@emotion/react";

import { SwitchCase } from "@/frameworks/libraries/components";
import { useProperties } from "@/frameworks/libraries/properties";
import { Extensions, Icon, Button } from "@/frameworks/designStuff/components";
import { useFileExtensions } from "@/frameworks/core/utils";
import { useFileSelect } from "@/modules/filebox/components/Files/FileItem/private/hooks";
import { ActionButtonWrapper } from "@/modules/filebox/components/Files/FileItem/private/FileCard/emotion";

import * as S from "./emotion";

export default function FileImage({
	file
}: {
	file: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number];
}) {
	const { getFileExtension } = useFileExtensions();
	const { handleSelectFile } = useFileSelect(file);
	const theme = useTheme();
	const fileType = getFileExtension(file.fileMimeType || "");
	const properties = useProperties();

	const getImageSrc = () => {
		if (fileType === "image") return `${properties.default.webDomain}${file.filePath}`;
		if (file.fileBoxType === "LINK") {
			if (!file.imageUrl) return;
			return file.imageUrl;
		}
		return file.docThumbnailPath;
	};

	return (
		<S.FileImage verticalAlign="center" horizonAlign="center" src={getImageSrc()}>
			<SwitchCase
				condition={file.fileBoxType}
				cases={{
					FOLDER: <Icon name="Folder24" size={72} fill={theme.colors.accent_50_100} />,
					LINK: (
						<S.LinkBadge>
							<Icon name="Link24" size={22} fill={theme.colors.neutral_30_100} />
						</S.LinkBadge>
					),
					FILE: (
						<S.ExtensionBadge>
							<Extensions type={getFileExtension(file.fileMimeType || "")} />
						</S.ExtensionBadge>
					)
				}}
			/>
			{file.fileBoxType !== "FOLDER" && (
				<ActionButtonWrapper className="action_buttons">
					<Button.Icon
						name="Search24"
						size="XXlarge"
						filled="transparent"
						fill="neutral_80_100"
						onClick={handleSelectFile}
					/>
				</ActionButtonWrapper>
			)}
		</S.FileImage>
	);
}
